import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import NightlightIcon from "@mui/icons-material/Nightlight";
import Loader from "../../components/Loading/Loader";
import { useBrand } from "../../context/BrandContext";
import { useTrainingRoomAccess } from "../../hooks/useTrainingRoomAccess";
import { useLiveTrainingSession } from "../../hooks/useLiveTrainingSession";
import { useCoachExerciseLiveActions } from "../../coaching/coachExerciseLiveActions";
import { useLiveTrainingPlayerSounds } from "../../hooks/useLiveTrainingPlayerSounds";
import { useEvalPreCaptureCountdown } from "../../hooks/useEvalPreCaptureCountdown";
import { useLiveTrainingSequenceTimer } from "../../hooks/useLiveTrainingSequenceTimer";
import { useOrientation } from "../../hooks/useOrientation";
import { useScreenWakeLock } from "../../hooks/useScreenWakeLock";
import {
  requestLiveFullscreen,
  useIsLiveFullscreen,
  useLiveFullscreen,
  isLiveFullscreenGateDismissed,
  dismissLiveFullscreenGate,
  useLiveTrainingPortalRoot,
} from "../../hooks/useLiveFullscreen";
import {
  TE_STATE_FINALIZED,
  TE_STATE_CANCELLED,
  TE_STATE_STARTED,
  TE_STATE_PAUSED,
  resolveExerciseStateIdFromRow,
} from "../../utils/coachExerciseNavigation";
import {
  sessionWindowRemainingMs,
  isWithinSessionWindow,
  parseSessionWindow,
} from "../../utils/liveTrainingSessionWindow";
import {
  clearCoachLiveTrainingAutoEnterSuppress,
  clearLiveTrainingContext,
  markCoachLeftLiveTraining,
  readLiveTrainingContext,
  writeLiveTrainingContext,
} from "../../utils/liveTrainingContext";
import { shouldPromptLiveAutoevalOnFinalize, isTeSequences, isOffTableExercise, isPersonalizedExercise, isKotExercise, isKingOfTableExerciseType } from "../../utils/teExecutionType";
import { coachToolsFromExercise } from "../../utils/teCoachTools";
import {
  isEvalVideoRecordingEnabled,
  isLiveTrainingDeviceEvalCaptureEnabled,
  isClientCameraRecordingEnabled,
  loadClientSettings,
  saveClientSettings,
  normalizeClientSettings,
} from "../../utils/liveTrainingSettings";
import { releaseHeldEvalCameraStream } from "../../utils/evalCameraPermission";
import { resolvePinnedTableContext, listTablesWithSeatedPlayers } from "../../utils/liveTrainingPlayerContext";
import {
  autoevolution,
  getexercise,
  updateexercise,
  createLiveTimerBlock,
} from "../../components/funtionsBD/Mainservice";
import { HOME_ACTIVE_ROLE_KEY, userHasModeratorRole, userIsExpertCoach } from "../../utils/homeRole";
import { suppressCoachLiveTrainingAutoEnter } from "../../utils/liveTrainingNav";
import { isDemoClub, isDemoWalkInUser, pinDemoLiveSession } from "../../utils/demoClub";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName } from "../../utils/localizedCatalogFields";
import { isMobileDevice } from "../../utils/isMobileDevice";
import { resolveDashboardPath } from "../../utils/liveTrainingDashboardPath";
import { resolveSessionCoachUserId } from "../../utils/liveTrainingPlayerContext";
import { filterPresentRoster, filterTablesForStation } from "../../utils/coachSessionTables";
import {
  presentPlayerVoiceHints,
  groupRosterNotPresentHints,
} from "../../utils/tableSetupVoiceCommands";
import { buildSimilarToLastSessionContext, buildCurrentSessionPlanContext } from "../../utils/coachSessionExerciseHistory";
import { formatExercisePlayerName } from "../../utils/evaluationVideoMediaMeta";
import {
  viewerWasActiveInSequence,
  pickSequenceRowForOrder,
  resolveCompletedSequenceOrder,
} from "../../utils/liveTrainingSequenceAutoeval";
import { ensureActiveClubForSession } from "../../utils/switchActiveClubClient";
import { LiveTrainingHeader } from "./LiveTrainingHeader";
import { LiveTrainingWaitingRoom } from "./LiveTrainingWaitingRoom";
import { LiveTrainingCoachEmptySetup } from "./LiveTrainingCoachEmptySetup";
import { LiveTrainingExerciseHero } from "./LiveTrainingExerciseHero";
import { LiveTrainingPlayerView } from "./LiveTrainingPlayerView";
import { exerciseForViewerExecution } from "../../utils/personalizedTrainingSlot";
import { isTypeOnlyBlock, isPlaceholderBlock, persistPlanningFocus, plannedBlockTitle } from "../../utils/planningSubgroups";
import { pickKotMatchSimulationExercise } from "../../utils/kotMatchSimulation";
import { LiveTrainingPlayerChronoReview } from "./LiveTrainingPlayerChronoReview";
import { LiveTrainingModeLiveCard } from "./LiveTrainingModeLiveCard";
import { LiveTrainingProgressRail } from "./LiveTrainingProgressRail";
import { LiveTrainingSessionPlanSheet } from "./LiveTrainingSessionPlanSheet";
import { LiveTrainingStationTabs } from "./LiveTrainingStationTabs";
import { LiveTrainingFeedbackSheet } from "./LiveTrainingFeedbackSheet";
import { LiveTrainingAutoevalSheet } from "./LiveTrainingAutoevalSheet";
import { LiveTrainingCoachBar } from "./LiveTrainingCoachBar";
import { CoachHistorySessionPlanDialog } from "../../components/planning/CoachHistorySessionPlanDialog";
import { SelectSessionTemplateDialog } from "../../components/planning/SelectSessionTemplateDialog";
import { TemplateApplyWizardDialog } from "../../components/planning/TemplateApplyWizardDialog";
import {
  getTrainingSessionTemplates,
  applyTrainingSessionTemplate,
} from "../../components/funtionsBD/Mainservice";
import {
  exercisesForStation,
  globalIndexForTeId,
  pickCoachStationControlIndex,
  stationLabel,
} from "../../utils/liveTrainingStations";
import { useKotCoachRound } from "../../hooks/useKotCoachRound";
import { LiveTrainingKotPanel } from "./LiveTrainingKotPanel";
import { LiveTrainingCoachDashboard } from "./LiveTrainingCoachDashboard";
import { LiveTrainingKotCoachParams } from "./LiveTrainingKotCoachParams";
import { LiveTrainingSessionCompletePanel } from "./LiveTrainingSessionCompletePanel";
import { LiveTrainingCaptureListener } from "./LiveTrainingCaptureListener";
import { LiveTrainingCaptureSummary } from "./LiveTrainingCaptureSummary";
import { resolveLiveSequence } from "../../utils/liveTrainingSequenceResolve";
import {
  captureSequenceOrder,
  resolveCapturePhase,
} from "../../utils/expertCapturePhase";
import PlayerPresenceDialog from "../../components/PlayerPresenceDialog";
import { LiveTrainingSessionDecisionPanel } from "./LiveTrainingSessionDecisionPanel";
import { LiveTrainingAddExerciseDialog } from "./LiveTrainingAddExerciseDialog";
import { LiveTrainingSettingsDialog } from "./LiveTrainingSettingsDialog";
import { LiveTrainingDuckMode } from "./LiveTrainingDuckMode";
import { getStoredCameraFacingMode } from "../../hooks/useLiveTrainingEvalRecorder";
import { LiveTrainingEvalCaptureHost } from "./LiveTrainingVideoCalibrationOverlay";
import { LiveTrainingEvalDebugPanel } from "./LiveTrainingEvalDebugPanel";
import { EvalCameraPermissionBanner } from "./EvalCameraPermissionBanner";
import "./EvalCameraPermissionBanner.css";
import { useEvalCameraPermissionGate } from "../../hooks/useEvalCameraPermissionGate";
import { useTableInstructionRecorder } from "../../hooks/useTableInstructionRecorder";
import { useOffTableChronoRecorder } from "../../hooks/useOffTableChronoRecorder";
import { useStationCameraPresence } from "../../hooks/useStationCameraPresence";
import { useTableCameraPresence } from "../../hooks/useTableCameraPresence";
import {
  tableNumbersFromSessionTables,
  tablePhoneCoveredTables,
} from "../../utils/tablePhoneCoverage";
import { PlayerNewTrainingSessionDialog } from "../../components/PlayerNewTrainingSessionDialog";
import { useTourDemoActive } from "../../tour/useTourDemoActive";
import { isPingfloTourRunning } from "../../tour/tourTarget";
import { isTourExitPending } from "../../tour/clearTourDemoState";
import { CLOSE_TOUR_TRANSIENT_UI_EVENT } from "../../tour/tourEvents";
import { LiveTrainingTourScaffold } from "./LiveTrainingTourScaffold";
import { ExerciseResultDialog } from "../../components/ExerciseResultDialog";
import "../../components/KotCoachStartRound.css";
import "../../components/KotSettings.css";
import "./LiveTrainingPage.css";
import "./LiveTrainingLightTheme.css";
import "./LiveTrainingVideoCalibrationOverlay.css";

function PlayerLiveFullscreenGate({ enabled }) {
  const { t } = useTranslation();
  const fullscreen = useIsLiveFullscreen();
  const [dismissed, setDismissed] = useState(() => isLiveFullscreenGateDismissed());

  const hideGate = useCallback(() => {
    dismissLiveFullscreenGate();
    setDismissed(true);
  }, []);

  const standalone =
    typeof window !== "undefined" &&
    (window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches);

  if (!enabled || fullscreen || standalone || dismissed) {
    return null;
  }

  const hideBrowserBar = () =>
    requestLiveFullscreen(undefined, { hideBrowserChrome: true });

  return (
    <button
      type="button"
      className="lt-fs-gate"
      onClick={() => {
        const pending = hideBrowserBar();
        Promise.resolve(pending).finally(hideGate);
      }}
    >
      <span className="lt-fs-gate__label">
        {t("liveTrainingTapFullscreen", "Tap to hide the browser bar")}
      </span>
    </button>
  );
}

export function LiveTrainingPage() {
  const isMobileLive = isMobileDevice();
  const [duckModeActive, setDuckModeActive] = useState(false);
  const [evalCaptureUi, setEvalCaptureUi] = useState(() => ({
    enabled: false,
    recording: false,
    uploading: false,
    debugSnapshot: null,
    facingMode: getStoredCameraFacingMode(),
    cameraReady: false,
  }));
  const cameraFacingMode = evalCaptureUi.facingMode || "environment";
  const isFrontCamera = cameraFacingMode === "user";

  // Front camera: keep screen awake, prevent dimming/sleep
  // Duck mode on back camera: keep wake lock active so OS does not sleep background camera stream
  const keepScreenAwake = isMobileLive || isFrontCamera || duckModeActive;
  useScreenWakeLock(keepScreenAwake);

  useEffect(() => {
    if (isFrontCamera && duckModeActive) {
      setDuckModeActive(false);
    }
  }, [isFrontCamera, duckModeActive]);

  const toggleDuckMode = useCallback(() => {
    if (isFrontCamera) return;
    setDuckModeActive((prev) => !prev);
  }, [isFrontCamera]);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { brand, refreshBrand } = useBrand();
  const tourDemo = useTourDemoActive();
  const livePortalRoot = useLiveTrainingPortalRoot();
  const orientation = useOrientation();
  const isLandscape = orientation === "landscape";

  const readStoredSession = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("rsessn") || "null");
      const ctx = readLiveTrainingContext();
      if (!stored?.ts_id) return null;
      if (
        ctx?.ts_id &&
        ctx?.gr_id &&
        String(ctx.ts_id) === String(stored.ts_id)
      ) {
        return { ...stored, gr_id: ctx.gr_id || stored.gr_id };
      }
      return stored;
    } catch {
      return null;
    }
  }, []);

  const [session, setSession] = useState(readStoredSession);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const syncSession = () => setSession(readStoredSession());
    window.addEventListener("storage", syncSession);
    window.addEventListener("smarttt-session-changed", syncSession);
    syncSession();
    return () => {
      window.removeEventListener("storage", syncSession);
      window.removeEventListener("smarttt-session-changed", syncSession);
    };
  }, [readStoredSession]);
  const [remainingMs, setRemainingMs] = useState(() =>
    session ? sessionWindowRemainingMs(session) : 0
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackRecipient, setFeedbackRecipient] = useState("all");
  const [feedbackSessionPlanAssist, setFeedbackSessionPlanAssist] =
    useState(false);
  const [feedbackBootstrapSeed, setFeedbackBootstrapSeed] = useState(null);
  const [feedbackSimilarLastSession, setFeedbackSimilarLastSession] =
    useState(null);
  const [feedbackCurrentSessionPlan, setFeedbackCurrentSessionPlan] =
    useState(null);
  const reopenPlanAfterPingfloRef = useRef(false);

  // Chat state — managed at page level so messages persist across sheet open/close
  // and badge counts accumulate even when the sheet is closed.
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPeerId, setChatPeerId] = useState(null); // coach's user_id (for players) / last player's user_id (for coach)
  const [chatUnread, setChatUnread] = useState(0);
  const [coachActionError, setCoachActionError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const coachStopRoundRef = useRef(null);
  const chatHistoryFetchedRef = useRef(false);
  const feedbackRecipientRef = useRef("all");

  // Track the raw home role selection so that multi-role users (coach/admin+player)
  // who pick "player" on the dashboard are always shown the player view — even if
  // their stored user object does not carry player-role flags (which would cause
  // useTrainingRoomAccess to still report isCoachView=true).
  const [storedHomeRole, setStoredHomeRole] = useState(
    () => { try { return localStorage.getItem(HOME_ACTIVE_ROLE_KEY); } catch { return null; } }
  );
  useEffect(() => {
    // Arrived intentionally (FAB, session card, login). Allow auto-enter again later
    // only after the next deliberate leave — not while staying on Home.
    clearCoachLiveTrainingAutoEnterSuppress();
  }, []);
  useEffect(() => {
    const sync = () => {
      try { setStoredHomeRole(localStorage.getItem(HOME_ACTIVE_ROLE_KEY)); } catch { /* ignore */ }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("smarttt-home-role-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("smarttt-home-role-changed", sync);
    };
  }, []);

  // Sequence ratings + competency-block finalize autoeval dialog
  const [submittedSeqKeys, setSubmittedSeqKeys] = useState(() => new Set());
  const [seqRatings, setSeqRatings] = useState([]); // [{ perf, exertion }]
  const seqRatingsRef = useRef([]);
  const autoevalInFlightRef = useRef(new Set());
  const autoevalSheetShownRef = useRef(new Set());
  const [autoevalExercise, setAutoevalExercise] = useState(null);
  const [autoevalRequired, setAutoevalRequired] = useState(false);
  useEffect(() => {
    seqRatingsRef.current = seqRatings;
  }, [seqRatings]);
  const wasActiveInCurrentBlockRef = useRef(false);
  const {
    socket,
    user,
    exercises,
    stations,
    multiStation,
    focusedTsgId,
    selectFocusedStation,
    currentExercise,
    currentIndex,
    setCurrentIndex,
    exerciseStateId,
    isKot,
    kotSnapshot,
    roster,
    viewerPlayerId,
    myTablePlayer,
    viewerTableNumber,
    viewerTableContext,
    sessionTables,
    viewerHr,
    viewerMaxHr,
    getHrSamples,
    getLiveBpm,
    requestHrSnapshot,
    liveSequenceRolesByTeId,
    socketSequenceMetaByTeId,
    currentSequenceIndexByTeId,
    viewerRole,
    viewerSide,
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    sequenceTransitionPauseRemaining,
    lastSequenceCompleted,
    loading,
    error,
    realtimeByTeId,
    sessionFinalized,
    awaitingSessionDecision,
    finalizeTrainingSession,
    reopenSessionAfterAddExercise,
    refreshExercises,
    requestRefresh,
    refreshLiveView,
    refreshing,
    viewRefreshToken,
  } = useLiveTrainingSession(session);

  const [addExerciseOpen, setAddExerciseOpen] = useState(false);
  const [addExerciseMode, setAddExerciseMode] = useState("add");
  const [replaceExerciseTarget, setReplaceExerciseTarget] = useState(null);
  const [sessionPlanSheetOpen, setSessionPlanSheetOpen] = useState(false);
  const [sessionPlanOpen, setSessionPlanOpen] = useState(false);
  const reopenPlanSheetAfterAddRef = useRef(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [templateApplyMode, setTemplateApplyMode] = useState("catalog");
  const [templateApplyWizardOpen, setTemplateApplyWizardOpen] = useState(false);
  const [captureListenPaused, setCaptureListenPaused] = useState(false);
  const [captureVoiceState, setCaptureVoiceState] = useState("idle");
  const [tablePingfloOpen, setTablePingfloOpen] = useState(false);
  const [captureSummaryOpen, setCaptureSummaryOpen] = useState(false);
  const captureSummaryShownRef = useRef(false);
  const [finalizingSession, setFinalizingSession] = useState(false);
  const [relaunchingFromDecision, setRelaunchingFromDecision] = useState(false);
  const [openPresenceTick, setOpenPresenceTick] = useState(0);
  const [overlayPresenceOpen, setOverlayPresenceOpen] = useState(false);
  const [startingLiveTimer, setStartingLiveTimer] = useState(false);
  const [openChronoTick, setOpenChronoTick] = useState(0);
  const [demoReviewTarget, setDemoReviewTarget] = useState(null);
  const [coachExerciseDetailOpen, setCoachExerciseDetailOpen] = useState(false);
  const [captureSettingsTick, setCaptureSettingsTick] = useState(0);
  useEffect(() => {
    const bump = () => setCaptureSettingsTick((n) => n + 1);
    window.addEventListener("smarttt:live-training-coach-settings", bump);
    window.addEventListener("smarttt:live-training-forced-constraints", bump);
    window.addEventListener("smarttt:live-training-client-settings", bump);
    return () => {
      window.removeEventListener("smarttt:live-training-coach-settings", bump);
      window.removeEventListener(
        "smarttt:live-training-forced-constraints",
        bump
      );
      window.removeEventListener("smarttt:live-training-client-settings", bump);
    };
  }, []);
  const evalOpenCalibrationRef = useRef(() => {});
  const evalCloseCalibrationRef = useRef(() => {});
  const handleEvalCaptureUi = useCallback((ui) => {
    if (typeof ui?.openCalibration === "function") {
      evalOpenCalibrationRef.current = ui.openCalibration;
    }
    if (typeof ui?.closeCalibration === "function") {
      evalCloseCalibrationRef.current = ui.closeCalibration;
    }
    setEvalCaptureUi((prev) => {
      const next = {
        enabled: Boolean(ui?.enabled),
        recording: Boolean(ui?.recording),
        uploading: Boolean(ui?.uploading),
        debugSnapshot: ui?.debugSnapshot || null,
        facingMode:
          ui?.facingMode || prev?.facingMode || getStoredCameraFacingMode(),
        cameraReady: Boolean(ui?.cameraReady),
      };
      if (
        prev.enabled === next.enabled &&
        prev.recording === next.recording &&
        prev.uploading === next.uploading &&
        prev.debugSnapshot === next.debugSnapshot &&
        prev.facingMode === next.facingMode &&
        prev.cameraReady === next.cameraReady
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const dismissTourDialogs = () => {
      setCreateOpen(false);
      setFeedbackOpen(false);
      setSettingsOpen(false);
      setAddExerciseOpen(false);
      setAddExerciseMode("add");
      setReplaceExerciseTarget(null);
      setSessionPlanSheetOpen(false);
      setSessionPlanOpen(false);
      setDemoReviewTarget(null);
      try {
        evalCloseCalibrationRef.current?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
    return () => window.removeEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
  }, []);

  const handleCoachStopRound = useCallback(() => {
    coachStopRoundRef.current?.();
  }, []);

  const registerCoachStopRound = useCallback((fn) => {
    coachStopRoundRef.current = fn;
  }, []);

  useEffect(() => {
    setCoachActionError(null);
  }, [currentExercise?.te_id]);

  const { isCoachView } = useTrainingRoomAccess(roster);

  // Prefer live history BPM (clears on hr_value=0) so strap reassignment updates the player view immediately.
  const effectiveViewerHr = useMemo(() => {
    const fromHistory = getLiveBpm?.(viewerPlayerId, user?.user_id);
    if (fromHistory != null) return fromHistory;
    return viewerHr;
  }, [getLiveBpm, viewerPlayerId, user?.user_id, viewerHr]);

  const demoClubLive = isDemoClub(brand, null, user);

  useEffect(() => {
    if (!demoClubLive || !isCoachView || !session?.ts_id) return;
    pinDemoLiveSession({
      ts_id: session.ts_id,
      gr_id: session.gr_id,
    });
  }, [demoClubLive, isCoachView, session?.ts_id, session?.gr_id]);
  const localeCode = currentAppLanguage(i18n);

  const openDemoExerciseReview = useCallback((exercise) => {
    if (!exercise?.te_id) return;
    setDemoReviewTarget({ exercise, sequenceOrder: null });
  }, []);

  const openDemoReviewItem = useCallback((item) => {
    if (!item?.exercise?.te_id) return;
    setDemoReviewTarget({
      exercise: item.exercise,
      sequenceOrder: item.sequenceOrder ?? null,
    });
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (isCoachView) {
      root.classList.add("lt-coach-live");
    } else {
      root.classList.remove("lt-coach-live");
    }
    return () => root.classList.remove("lt-coach-live");
  }, [isCoachView]);

  const kotRound = useKotCoachRound({
    socket,
    tsId: session?.ts_id,
    teId: currentExercise?.te_id,
    exerciseStateId,
    externalSnapshot: kotSnapshot,
    enabled: Boolean(
      isCoachView &&
        isKot &&
        socket &&
        session?.ts_id &&
        currentExercise?.te_id
    ),
    stopRoundMode: "coach",
  });

  const presentPlayers = useMemo(
    () => filterPresentRoster(roster).filter((p) => p?.user_id != null),
    [roster]
  );

  const autoevalPlayerName = useMemo(() => {
    const fromRoster =
      viewerPlayerId != null
        ? (roster || []).find(
            (r) => String(r.player_id) === String(viewerPlayerId)
          )
        : null;
    return formatExercisePlayerName(
      myTablePlayer || fromRoster || user,
      viewerPlayerId || user?.user_id || user?.player_id
    );
  }, [myTablePlayer, roster, viewerPlayerId, user]);

  // ── Chat: page-level subscription ────────────────────────────────────────
  const sessionCoachUserId = useMemo(
    () => resolveSessionCoachUserId(session),
    [session]
  );

  const fetchChatHistory = useCallback(
    (recipient) => {
      if (!socket || !currentExercise?.te_id || !user?.user_id) return;

      if (isCoachView) {
        if (recipient === "all") {
          socket.emit("getPreviousMessages", {
            te_id: currentExercise.te_id,
            ts_id: session?.ts_id,
            sender_id: user.user_id,
            coach_inbox: true,
          });
          return;
        }
        if (recipient) {
          socket.emit("getPreviousMessages", {
            te_id: currentExercise.te_id,
            ts_id: session?.ts_id,
            sender_id: user.user_id,
            receiver_id: recipient,
          });
        }
        return;
      }

      if (!recipient) return;
      socket.emit("getPreviousMessages", {
        te_id: currentExercise.te_id,
        ts_id: session?.ts_id,
        sender_id: user.user_id,
        receiver_id: recipient,
      });
    },
    [socket, currentExercise?.te_id, session?.ts_id, user?.user_id, isCoachView]
  );

  // Players: coach user_id comes from session metadata (connectedUsers never lists coaches).
  useEffect(() => {
    if (isCoachView || !sessionCoachUserId) return;
    setChatPeerId((prev) => prev ?? sessionCoachUserId);
  }, [isCoachView, sessionCoachUserId]);

  useEffect(() => {
    if (isCoachView || !sessionCoachUserId || chatHistoryFetchedRef.current) return;
    if (!socket || !currentExercise?.te_id || !user?.user_id) return;
    chatHistoryFetchedRef.current = true;
    fetchChatHistory(sessionCoachUserId);
  }, [
    isCoachView,
    sessionCoachUserId,
    socket,
    currentExercise?.te_id,
    user?.user_id,
    fetchChatHistory,
  ]);

  useEffect(() => {
    feedbackRecipientRef.current = feedbackRecipient;
  }, [feedbackRecipient]);

  // Keep the newMessage listener alive regardless of whether the sheet is open
  // so the unread badge increments even when the player/coach hasn't opened chat.
  useEffect(() => {
    if (!socket || !currentExercise?.te_id) return;
    const teId = currentExercise.te_id;

    const onConnectedUsers = (users) => {
      if (isCoachView) return;
      const peer = users?.find((u) => u.role === "coach");
      if (peer?.user_id) {
        setChatPeerId(peer.user_id);
      }
    };

    const onPreviousMessages = (msgs) => {
      setChatMessages(Array.isArray(msgs) ? msgs : []);
    };

    const onNewMessage = (msg) => {
      const senderId = msg.sender_id ?? msg.sender?.user_id;
      const receiverId = msg.receiver_id ?? msg.receiver?.user_id;
      const me = user?.user_id;

      if (isCoachView && feedbackRecipientRef.current !== "all" && me) {
        const peer = String(feedbackRecipientRef.current);
        const inThread =
          (String(senderId) === String(me) && String(receiverId) === peer) ||
          (String(senderId) === peer && String(receiverId) === String(me));
        const isBroadcast = receiverId == null || receiverId === "";
        if (!inThread && !isBroadcast) return;
      }

      setChatMessages((prev) => {
        if (msg.message_id && prev.some((m) => m.message_id === msg.message_id)) {
          return prev;
        }
        return [...prev, msg];
      });
      const fromPeer = senderId != null && String(senderId) !== String(me);
      if (fromPeer) {
        setChatUnread((n) => n + 1);
        if (isCoachView) setChatPeerId(senderId);
      }
    };

    socket.on("connectedUsers", onConnectedUsers);
    socket.on("previousMessages", onPreviousMessages);
    socket.on("newMessage", onNewMessage);
    socket.emit("getConnectedUsers", { te_id: teId });

    return () => {
      socket.off("connectedUsers", onConnectedUsers);
      socket.off("previousMessages", onPreviousMessages);
      socket.off("newMessage", onNewMessage);
    };
  }, [socket, currentExercise?.te_id, user?.user_id, isCoachView]);

  // Reset chat state when exercise changes
  useEffect(() => {
    setChatMessages([]);
    setChatUnread(0);
    chatHistoryFetchedRef.current = false;
    if (isCoachView) {
      setFeedbackRecipient("all");
      setChatPeerId(null);
    } else if (sessionCoachUserId) {
      setChatPeerId(sessionCoachUserId);
    } else {
      setChatPeerId(null);
    }
  }, [currentExercise?.te_id, isCoachView, sessionCoachUserId]);

  useEffect(() => {
    if (!isCoachView || !feedbackOpen) return;
    fetchChatHistory(feedbackRecipient);
  }, [isCoachView, feedbackOpen, feedbackRecipient, fetchChatHistory]);

  const handleFeedbackRecipientChange = useCallback((nextRecipient) => {
    setFeedbackRecipient(nextRecipient);
    setChatMessages([]);
  }, []);

  const handleCoachTableFeedbackSent = useCallback(
    ({ receiverUserId }) => {
      if (!receiverUserId) return;
      setChatPeerId(String(receiverUserId));
      if (feedbackRecipientRef.current !== "all") {
        setFeedbackRecipient(String(receiverUserId));
      }
    },
    []
  );

  const liveClubId =
    session?.club_id ||
    session?.tbl_group?.club_id ||
    brand?.club_id ||
    user?.club_id ||
    user?.club_details?.club_id ||
    null;
  const liveClubName =
    session?.club_name ||
    session?.tbl_club?.club_name ||
    brand?.club_name ||
    user?.club_details?.club_name ||
    user?.club_name ||
    null;
  const liveGroupName =
    session?.gr_name ||
    session?.group_name ||
    session?.tbl_group?.gr_name ||
    null;

  const openFeedback = useCallback(() => {
    setChatUnread(0);
    setFeedbackSessionPlanAssist(false);
    setFeedbackBootstrapSeed(null);
    setFeedbackSimilarLastSession(null);
    setFeedbackCurrentSessionPlan(
      buildCurrentSessionPlanContext(exercises, {
        language: localeCode,
        stations,
      })
    );
    persistPlanningFocus({
      page: "live_training",
      ts_id: session?.ts_id || null,
      gr_id: session?.gr_id || null,
      te_id: currentExercise?.te_id || null,
      tsg_id: focusedTsgId || currentExercise?.tsg_id || null,
      exercise_name: plannedBlockTitle(currentExercise, localeCode) || null,
    });
    reopenPlanAfterPingfloRef.current = false;
    if (isCoachView && chatPeerId) {
      setFeedbackRecipient(String(chatPeerId));
    }
    setFeedbackOpen(true);
    if (!isCoachView) {
      const peerId = chatPeerId || sessionCoachUserId;
      if (peerId) fetchChatHistory(peerId);
    }
    if (socket && currentExercise?.te_id) {
      socket.emit("getConnectedUsers", { te_id: currentExercise.te_id });
    }
  }, [
    chatPeerId,
    isCoachView,
    sessionCoachUserId,
    fetchChatHistory,
    socket,
    currentExercise,
    exercises,
    stations,
    localeCode,
    session?.ts_id,
    session?.gr_id,
    focusedTsgId,
  ]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!session?.ts_id) {
      // Pingflo walks /live-training with a stand-in table when the hall is empty.
      // Do not bounce to Home or the overlay stays on the dashboard.
      if (tourDemo || isPingfloTourRunning() || isTourExitPending() || isDemoWalkInUser()) return;
      navigate("/home", { replace: true });
      return;
    }
    const w = parseSessionWindow(session);
    if (w) {
      writeLiveTrainingContext({
        ts_id: session.ts_id,
        gr_id: session.gr_id,
        windowEndMs: w.endMs,
      });
    }
  }, [session, navigate, tourDemo]);

  // Track active-role status while a sequence block is running (before roles swap to upcoming block).
  useEffect(() => {
    if (
      awaitingManualSequenceStart ||
      awaitingAutoSequenceStart ||
      !isTeSequences(currentExercise) ||
      isKot ||
      !viewerPlayerId
    ) {
      return;
    }
    const runningOrder = resolveCompletedSequenceOrder(currentExercise, {
      awaitingManualSequenceStart: false,
      awaitingAutoSequenceStart: false,
      lastSequenceCompleted: false,
    });
    const seq = pickSequenceRowForOrder(
      currentExercise,
      runningOrder,
      viewerTableNumber
    );
    wasActiveInCurrentBlockRef.current = viewerWasActiveInSequence(
      seq,
      viewerPlayerId,
      user?.user_id
    );
  }, [
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    currentExercise,
    isKot,
    viewerPlayerId,
    viewerTableNumber,
    user?.user_id,
  ]);

  const applyRemoteSequenceAutoeval = useCallback(
    (detail) => {
      // Record remote ratings for silent averages, but never open a player dialog.
      if (!detail?.sequence_key || !detail?.user_id) return;
      if (String(detail.user_id) !== String(user?.user_id)) return;
      if (
        detail.te_id &&
        currentExercise?.te_id &&
        String(detail.te_id) !== String(currentExercise.te_id)
      ) {
        return;
      }

      const key = String(detail.sequence_key);
      if (submittedSeqKeys.has(key)) return;

      setSubmittedSeqKeys((prev) => new Set([...prev, key]));
      const perf = Number(detail.perf);
      const exertion = Number(detail.exertion);
      if (Number.isFinite(perf) && Number.isFinite(exertion)) {
        setSeqRatings((prev) => [...prev, { perf, exertion }]);
      }
    },
    [user?.user_id, currentExercise?.te_id, submittedSeqKeys]
  );

  useEffect(() => {
    const onRemote = (e) => applyRemoteSequenceAutoeval(e.detail);
    window.addEventListener("smarttt-sequence-autoeval-submitted", onRemote);
    return () =>
      window.removeEventListener("smarttt-sequence-autoeval-submitted", onRemote);
  }, [applyRemoteSequenceAutoeval]);

  useEffect(() => {
    if (!session) return undefined;
    const tick = () => setRemainingMs(sessionWindowRemainingMs(session));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [session]);

  useEffect(() => {
    const clampRating = (n) =>
      Math.min(5, Math.max(1, Math.round(Number(n)) || 1));

    const postAutoeval = (teId, perf, exertion) => {
      const teKey = String(teId);
      if (autoevalInFlightRef.current.has(teKey)) return;
      autoevalInFlightRef.current.add(teKey);
      autoevolution({
        te_id: teId,
        exercise_perf: String(clampRating(perf)),
        perceived_exertion: String(clampRating(exertion)),
      }).finally(() => {
        autoevalInFlightRef.current.delete(teKey);
      });
    };

    const onFinalized = (e) => {
      const teId = e.detail?.te_id;
      if (!teId || isCoachView) return;
      const teKey = String(teId);

      const ratings = seqRatingsRef.current;
      const wasActive =
        wasActiveInCurrentBlockRef.current || ratings.length > 0;
      const ex =
        (exercises || []).find((x) => String(x.te_id) === teKey) ||
        (String(currentExercise?.te_id) === teKey ? currentExercise : null);

      // Competency blocks and personalized exercises. King of the Table is excluded.
      // Personalized drills from the current cycle are required; other blocks can be skipped.
      if (ex && shouldPromptLiveAutoevalOnFinalize(ex)) {
        if (ratings.length > 0) {
          const avgPerf = Math.round(
            ratings.reduce((s, r) => s + r.perf, 0) / ratings.length
          );
          const avgExertion = Math.round(
            ratings.reduce((s, r) => s + r.exertion, 0) / ratings.length
          );
          postAutoeval(teId, avgPerf, avgExertion);
          setSeqRatings([]);
          return;
        }
        if (
          !autoevalSheetShownRef.current.has(teKey) &&
          !ex.auto_evaluation
        ) {
          autoevalSheetShownRef.current.add(teKey);
          setAutoevalRequired(isPersonalizedExercise(ex));
          setAutoevalExercise(ex);
        }
        setSeqRatings([]);
        return;
      }

      // Other sequence exercises: keep silent averages only when ratings exist.
      if (isTeSequences(currentExercise) && wasActive) {
        if (ratings.length > 0) {
          const avgPerf = Math.round(
            ratings.reduce((s, r) => s + r.perf, 0) / ratings.length
          );
          const avgExertion = Math.round(
            ratings.reduce((s, r) => s + r.exertion, 0) / ratings.length
          );
          postAutoeval(teId, avgPerf, avgExertion);
        }
        setSeqRatings([]);
      }
    };
    window.addEventListener("smarttt-exercise-finalized", onFinalized);
    return () => window.removeEventListener("smarttt-exercise-finalized", onFinalized);
  }, [isCoachView, currentExercise, exercises]);

  const keepSessionClubActive = useCallback(async () => {
    let sessionClubId = liveClubId;
    if (!sessionClubId) {
      try {
        const grp = JSON.parse(localStorage.getItem("grpinf") || "null");
        sessionClubId =
          grp?.groupDetails?.club_id || grp?.club_id || sessionClubId;
      } catch {
        /* ignore */
      }
    }
    if (!sessionClubId) return;
    try {
      await ensureActiveClubForSession(sessionClubId, refreshBrand);
    } catch (err) {
      console.warn("Keep session club active failed:", err?.message || err);
    }
  }, [liveClubId, refreshBrand]);

  const handleGoHome = useCallback(async () => {
    if (session?.ts_id) {
      suppressCoachLiveTrainingAutoEnter(session.ts_id);
    }
    markCoachLeftLiveTraining(session?.ts_id);
    await keepSessionClubActive();
    navigate("/home", { replace: true });
  }, [navigate, session?.ts_id, keepSessionClubActive]);

  /** Demo players leave a closed session and wait on /demo for the next one. */
  const handleGoDemoNext = useCallback(() => {
    clearLiveTrainingContext();
    try {
      localStorage.removeItem("grpinf");
      localStorage.removeItem("sesinf");
    } catch {
      /* ignore */
    }
    navigate("/demo?wait=1", { replace: true });
  }, [navigate]);

  const handleDashboard = useCallback(async () => {
    markCoachLeftLiveTraining(session?.ts_id);
    await keepSessionClubActive();
    const path = resolveDashboardPath(user);
    navigate(path);
  }, [navigate, user, session?.ts_id, keepSessionClubActive]);

  const handleRefreshView = useCallback((event) => {
    requestLiveFullscreen(event);
    setCoachActionError(null);
    refreshLiveView({ silent: false });
  }, [refreshLiveView]);

  const isExerciseActive =
    exerciseStateId === TE_STATE_STARTED || exerciseStateId === TE_STATE_PAUSED;
  const showWaiting = !isExerciseActive && isWithinSessionWindow(session);

  // Show player view when the user is NOT in coach mode.
  // Use two signals:
  //   1. isCoachView — from useTrainingRoomAccess (validates role against profile flags)
  //   2. storedHomeRole — raw localStorage preference; trusted unconditionally so that
  //      multi-role users (coach/admin + player) who pick "player" on the dashboard
  //      always get the player UI even when their profile object lacks player-role flags.
  // Also don't gate on isWithinSessionWindow: a player should always see the player
  // view while on this page regardless of the scheduled session window.
  const usePlayerView = storedHomeRole === "player" || !isCoachView;
  // Player phones: request document fullscreen / collapse the browser chrome,
  // and keep the layout sized to the visual viewport when the bar stays visible.
  useLiveFullscreen(isMobileLive && !tourDemo, {
    force: Boolean(isMobileLive && usePlayerView && !tourDemo),
  });

  /** Player header refresh: fullscreen without the browser bar, then reload brand + live data. */
  const handlePlayerRefresh = useCallback(() => {
    // Must be issued before any await so the click still counts as the user gesture.
    requestLiveFullscreen(undefined, { hideBrowserChrome: true });
    setCoachActionError(null);
    void refreshBrand?.();
    refreshLiveView({ silent: false });
  }, [refreshBrand, refreshLiveView]);
  const playerExecutionExercise = useMemo(
    () =>
      exerciseForViewerExecution(
        currentExercise,
        viewerPlayerId,
        user?.user_id
      ),
    [currentExercise, viewerPlayerId, user?.user_id]
  );

  useLiveTrainingPlayerSounds({
    enabled: Boolean(session?.ts_id && currentExercise?.te_id),
    socket,
  });

  const sequenceTimer = useLiveTrainingSequenceTimer({
    exercise: currentExercise,
    exerciseStateId,
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    currentSequenceIndex:
      currentExercise?.te_id != null
        ? currentSequenceIndexByTeId[String(currentExercise.te_id)]
        : null,
  });

  useEvalPreCaptureCountdown({
    enabled:
      !isKot &&
      isTeSequences(currentExercise) &&
      sequenceTimer.isStarted &&
      !sequenceTimer.isWaiting &&
      !sequenceTimer.isPaused,
    tsId: session?.ts_id,
    teId: currentExercise?.te_id,
    sequenceNumber: sequenceTimer.seqNumber,
    sequenceRemainingSec: sequenceTimer.seqDisplay,
    sequenceDurationSec: sequenceTimer.sequenceDurationSec,
  });

  const evalVideoCaptureEnabled = isEvalVideoRecordingEnabled(session?.ts_id);
  // captureSettingsTick forces re-read after settings dialog save / FAB toggle.
  void captureSettingsTick;
  // Gate on isCoachView (coach UI / settings), not usePlayerView — dual-role
  // accounts can diverge and incorrectly open the player capture path.
  // Player path also warms the camera when instant replay is on.
  const deviceEvalCaptureEnabled = isLiveTrainingDeviceEvalCaptureEnabled(
    session?.ts_id,
    { isCoachView }
  );
  const canSeeEvalCaptureDebug =
    userHasModeratorRole(user) ||
    Number(user?.type_user_id) === 1 ||
    user?.isSuperAdmin === true;

  const clientCapture = loadClientSettings();
  const tablePhoneOn =
    usePlayerView &&
    clientCapture.tablePhoneEnabled === true &&
    Number(clientCapture.tablePhoneNumber) > 0;
  // Warm getUserMedia for eval capture AND table-phone coach Record (Android
  // Chrome rejects socket-triggered getUserMedia without a prior Allow tap).
  const needsClientCameraPermission =
    usePlayerView && (deviceEvalCaptureEnabled || tablePhoneOn);
  const evalCameraPermission = useEvalCameraPermissionGate({
    enabled: needsClientCameraPermission,
    // Table phones need the back camera ready for coach Record (esp. iOS).
    facingMode: tablePhoneOn ? "environment" : null,
  });

  useEffect(() => {
    if (isCoachView && !deviceEvalCaptureEnabled && !tablePhoneOn) {
      releaseHeldEvalCameraStream();
    }
  }, [isCoachView, deviceEvalCaptureEnabled, tablePhoneOn]);

  const togglePlayerCameraRecording = useCallback(() => {
    const next = !(loadClientSettings().cameraRecordingEnabled !== false);
    saveClientSettings(
      normalizeClientSettings({
        ...loadClientSettings(),
        cameraRecordingEnabled: next,
      })
    );
    setCaptureSettingsTick((n) => n + 1);
  }, []);
  const stationPhoneOn =
    usePlayerView &&
    clientCapture.stationPhoneEnabled === true &&
    Boolean(clientCapture.stationPhoneTsgId);
  const showKotPlayerArena =
    usePlayerView && isKot && Boolean(currentExercise?.te_id) && !stationPhoneOn;
  const pinnedTableContext = tablePhoneOn
    ? resolvePinnedTableContext(
        sessionTables,
        clientCapture.tablePhoneNumber,
        currentExercise,
        roster,
        viewerPlayerId,
        user?.user_id
      )
    : null;
  const activeTableContext = pinnedTableContext || viewerTableContext;
  const activeTableNumber = tablePhoneOn
    ? pinnedTableContext?.tableNumber ?? clientCapture.tablePhoneNumber
    : viewerTableNumber;
  const activeCameraSide = tablePhoneOn
    ? clientCapture.tablePhoneSide || "A"
    : viewerSide;
  const tablePhoneTablesKey = tablePhoneOn
    ? tablePhoneCoveredTables(clientCapture).join(",")
    : "";
  const tablePhoneTables = useMemo(
    () => (tablePhoneTablesKey ? tablePhoneTablesKey.split(",").map(Number) : []),
    [tablePhoneTablesKey]
  );
  const tablePhonePlayers = useMemo(() => {
    if (!tablePhoneOn || tablePhoneTables.length === 0) return null;
    const main = Number(activeTableNumber);
    const order = [
      ...tablePhoneTables.filter((n) => n === main),
      ...tablePhoneTables.filter((n) => n !== main),
    ];
    const players = [];
    for (const n of order) {
      const ctx = resolvePinnedTableContext(
        sessionTables,
        n,
        currentExercise,
        roster,
        viewerPlayerId,
        user?.user_id
      );
      for (const player of ctx?.players || []) {
        if (player?.playerId || player?.userId) players.push(player);
      }
    }
    return players;
  }, [
    tablePhoneOn,
    tablePhoneTables,
    activeTableNumber,
    sessionTables,
    currentExercise,
    roster,
    viewerPlayerId,
    user?.user_id,
  ]);
  const sessionTableNumbers = useMemo(
    () => tableNumbersFromSessionTables(sessionTables),
    [sessionTables]
  );
  const updateTablePhoneCoverage = useCallback((update) => {
    saveClientSettings(normalizeClientSettings(update(loadClientSettings())));
    setCaptureSettingsTick((n) => n + 1);
  }, []);
  const stationPhoneLabel = useMemo(() => {
    if (!stationPhoneOn || !clientCapture.stationPhoneTsgId) return null;
    const st = (stations || []).find(
      (s) => String(s?.tsg_id) === String(clientCapture.stationPhoneTsgId)
    );
    if (!st) return null;
    return st.is_main
      ? t("mainGroup", "Main group")
      : st.name || String(st.tsg_id);
  }, [stationPhoneOn, clientCapture.stationPhoneTsgId, stations, t]);
  const stationRecordingTables = useMemo(() => {
    if (!stationPhoneOn) return [];
    const scoped = filterTablesForStation(
      sessionTables,
      clientCapture.stationPhoneTsgId,
      stations
    );
    return listTablesWithSeatedPlayers(
      scoped,
      currentExercise,
      roster,
      viewerPlayerId,
      user?.user_id
    );
  }, [
    stationPhoneOn,
    sessionTables,
    clientCapture.stationPhoneTsgId,
    stations,
    currentExercise,
    roster,
    viewerPlayerId,
    user?.user_id,
  ]);
  const { stationCamerasByTsgId } = useStationCameraPresence({
    socket,
    tsId: session?.ts_id,
    enabled: stationPhoneOn && Boolean(socket) && Boolean(session?.ts_id),
    tsgId: stationPhoneOn ? clientCapture.stationPhoneTsgId : null,
    userId: user?.user_id || null,
    playerId: viewerPlayerId || null,
    label: stationPhoneLabel,
    listen: Boolean(isCoachView && socket && session?.ts_id),
  });
  const hasActiveTableCamera =
    !tourDemo &&
    (tablePhoneOn || (usePlayerView && isClientCameraRecordingEnabled())) &&
    Number(activeTableNumber) > 0;
  const { tablesWithCamera, phoneLimit: tablePhoneLimit } = useTableCameraPresence({
    socket,
    tsId: session?.ts_id,
    enabled:
      hasActiveTableCamera &&
      Boolean(socket) &&
      Boolean(session?.ts_id),
    tableNumber: hasActiveTableCamera ? activeTableNumber : null,
    tableNumbers: tablePhoneOn ? tablePhoneTables : null,
    userId: user?.user_id || null,
    listen: Boolean(isCoachView && socket && session?.ts_id),
  });
  // Exercise-instruction clips only. Coach table autoeval capture is handled by
  // LiveTrainingEvalCaptureHost → useLiveTrainingEvalRecorder (debug panel + upload).
  const tableInstructionCapture = useTableInstructionRecorder({
    socket,
    enabled:
      Boolean(socket) &&
      !tourDemo &&
      usePlayerView &&
      Number(activeTableNumber) > 0 &&
      (tablePhoneOn || isClientCameraRecordingEnabled()),
    tableNumber: Number(activeTableNumber) > 0 ? activeTableNumber : null,
    tableNumbers: tablePhoneTables,
    cameraSide: activeCameraSide,
    videoQuality: clientCapture.videoQuality || "low",
  });
  // Station / table / player camera phones for off-table chrono and coach
  // on-demand station clips. The managing coach Live Training UI never records.
  useOffTableChronoRecorder({
    socket,
    enabled:
      Boolean(socket) &&
      Boolean(session?.ts_id) &&
      !tourDemo &&
      (tablePhoneOn ||
        stationPhoneOn ||
        (usePlayerView && isClientCameraRecordingEnabled())),
    tsId: session?.ts_id || null,
    viewerUserId: user?.user_id || null,
    tableNumber: tablePhoneOn ? activeTableNumber : null,
    tableNumbers: tablePhoneTables,
    cameraSide: activeCameraSide,
    videoQuality: clientCapture.videoQuality || "low",
  });
  const tableLabel = activeTableNumber != null
    ? t("table_with_number", { number: activeTableNumber })
    : null;

  const coachStationExercises = useMemo(() => {
    if (!isCoachView) return exercises;
    return exercisesForStation(
      exercises,
      multiStation ? focusedTsgId : null
    );
  }, [isCoachView, exercises, multiStation, focusedTsgId]);

  // When the coach switches stations, move the global control index onto that
  // station's active/next block so hero, dashboard, and timers all follow.
  useEffect(() => {
    if (!isCoachView || !multiStation || !focusedTsgId) return;
    if (
      currentExercise &&
      String(currentExercise.tsg_id || "") === String(focusedTsgId)
    ) {
      return;
    }
    const idx = pickCoachStationControlIndex(
      exercises,
      realtimeByTeId,
      focusedTsgId
    );
    if (idx >= 0 && idx !== currentIndex) {
      setCurrentIndex(idx);
    }
  }, [
    isCoachView,
    multiStation,
    focusedTsgId,
    exercises,
    realtimeByTeId,
    currentExercise,
    currentIndex,
    setCurrentIndex,
  ]);

  const coachStationIndex = useMemo(() => {
    if (!coachStationExercises.length) return 0;
    if (!currentExercise?.te_id) return 0;
    const idx = coachStationExercises.findIndex(
      (ex) => String(ex.te_id) === String(currentExercise.te_id)
    );
    return idx >= 0 ? idx : 0;
  }, [coachStationExercises, currentExercise?.te_id]);

  /** Coach UI block for the focused station (never another station's TE). */
  const coachViewExercise = useMemo(() => {
    if (!isCoachView) return currentExercise;
    if (!multiStation || !focusedTsgId) return currentExercise;
    if (
      currentExercise &&
      String(currentExercise.tsg_id || "") === String(focusedTsgId)
    ) {
      return currentExercise;
    }
    return coachStationExercises[coachStationIndex] || coachStationExercises[0] || null;
  }, [
    isCoachView,
    multiStation,
    focusedTsgId,
    currentExercise,
    coachStationExercises,
    coachStationIndex,
  ]);

  const coachViewExerciseStateId = useMemo(() => {
    if (!coachViewExercise) return null;
    if (
      currentExercise?.te_id != null &&
      String(currentExercise.te_id) === String(coachViewExercise.te_id)
    ) {
      return exerciseStateId;
    }
    return resolveExerciseStateIdFromRow(coachViewExercise, realtimeByTeId);
  }, [
    coachViewExercise,
    currentExercise?.te_id,
    exerciseStateId,
    realtimeByTeId,
  ]);

  const coachViewIsKot =
    isKotExercise(coachViewExercise) ||
    (Boolean(isKot) &&
      coachViewExercise?.te_id != null &&
      currentExercise?.te_id != null &&
      String(currentExercise.te_id) === String(coachViewExercise.te_id));

  /** Empty session / empty focused station: coach setup, never the player wait room. */
  const showCoachEmptySetup =
    isCoachView &&
    !usePlayerView &&
    !loading &&
    !sessionFinalized &&
    !coachViewExercise;

  const kotMatchSimAssignRef = useRef(new Set());

  useEffect(() => {
    if (!isCoachView) return;
    const block = coachStationExercises[coachStationIndex];
    if (!block?.te_id || !isPlaceholderBlock(block)) return;
    const kotTypeOnly =
      isKotExercise(block) ||
      isKingOfTableExerciseType(block.type_ex || block.tbl_type_ex);
    if (!kotTypeOnly) return;
    const teId = String(block.te_id);
    if (kotMatchSimAssignRef.current.has(teId)) return;
    kotMatchSimAssignRef.current.add(teId);
    let cancelled = false;
    (async () => {
      try {
        const typeId = block.type_id || block.type_ex?.type_id || null;
        const response = await getexercise({
          type_id: typeId || undefined,
          group_id: session?.gr_id || null,
        });
        const picked = pickKotMatchSimulationExercise(response?.data?.data);
        if (!picked?.ex_id || cancelled) return;
        await updateexercise({
          te_id: block.te_id,
          replace_ex_id: picked.ex_id,
          existing_ex_id: block.ex_id || undefined,
        });
        if (cancelled) return;
        await refreshExercises();
        await refreshLiveView?.({ silent: true });
      } catch {
        kotMatchSimAssignRef.current.delete(teId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isCoachView,
    coachStationExercises,
    coachStationIndex,
    session?.gr_id,
    refreshExercises,
    refreshLiveView,
  ]);

  const coachStationFinalizedCount = useMemo(
    () =>
      coachStationExercises.filter(
        (ex) =>
          resolveExerciseStateIdFromRow(ex, realtimeByTeId) === TE_STATE_FINALIZED
      ).length,
    [coachStationExercises, realtimeByTeId]
  );

  const focusedStation = useMemo(
    () =>
      (stations || []).find(
        (s) => String(s.tsg_id) === String(focusedTsgId || "")
      ) || null,
    [stations, focusedTsgId]
  );

  const sessionPlanPresentNames = useMemo(
    () => presentPlayerVoiceHints(filterPresentRoster(roster)),
    [roster]
  );
  const sessionPlanNotPresentNames = useMemo(
    () => groupRosterNotPresentHints(roster),
    [roster]
  );

  const openSessionPlanPingflo = useCallback(async () => {
    if (!session?.ts_id) return;
    reopenPlanAfterPingfloRef.current = true;
    setSessionPlanSheetOpen(false);

    const currentPlan = buildCurrentSessionPlanContext(exercises, {
      language: localeCode,
      stations,
    });
    setFeedbackCurrentSessionPlan(currentPlan);

    let similar = null;
    if (session?.gr_id) {
      try {
        similar = await buildSimilarToLastSessionContext({
          grId: session.gr_id,
          excludeTsId: session.ts_id,
          language: localeCode,
        });
      } catch {
        similar = { available: false, reason: "load_failed" };
      }
    } else {
      similar = { available: false, reason: "missing_group" };
    }

    setFeedbackSimilarLastSession(similar);
    setFeedbackSessionPlanAssist(true);
    setFeedbackBootstrapSeed({
      seedKey: currentPlan.available
        ? "digipingLiveSessionPlanRefineSeed"
        : "digipingLiveSessionPlanSeed",
      visible: false,
      home_role: "coach",
      token: `session-plan-${session.ts_id}-${Date.now()}`,
    });
    persistPlanningFocus({
      page: "live_training",
      ts_id: session.ts_id,
      gr_id: session.gr_id || null,
      te_id: currentExercise?.te_id || null,
      tsg_id: focusedTsgId || currentExercise?.tsg_id || null,
      exercise_name: plannedBlockTitle(currentExercise, localeCode) || null,
    });
    setFeedbackOpen(true);
  }, [session?.ts_id, session?.gr_id, localeCode, exercises, stations, currentExercise, focusedTsgId]);

  const closeFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackSessionPlanAssist(false);
    setFeedbackBootstrapSeed(null);
    setFeedbackSimilarLastSession(null);
    setFeedbackCurrentSessionPlan(null);
    if (reopenPlanAfterPingfloRef.current) {
      reopenPlanAfterPingfloRef.current = false;
      setSessionPlanSheetOpen(true);
    }
  }, []);

  const selectCoachExercise = useCallback(
    (ex) => {
      if (!ex?.te_id) return;
      // Focus the station first so filtered lists update, then pin the TE.
      if (ex.tsg_id != null && multiStation) {
        selectFocusedStation(ex.tsg_id);
      }
      const idx = globalIndexForTeId(exercises, ex.te_id);
      if (idx >= 0) setCurrentIndex(idx);
    },
    [exercises, multiStation, selectFocusedStation, setCurrentIndex]
  );

  const coachDecisionActions = useCoachExerciseLiveActions({
    socket,
    tsId: session?.ts_id,
    requestRefresh,
    coachRole: true,
    teId: currentExercise?.te_id || null,
  });

  const relaunchCoachExercise = useCallback(
    async (targetExercise, { selectFirst = false } = {}) => {
      if (!targetExercise?.te_id || relaunchingFromDecision) return;
      setRelaunchingFromDecision(true);
      setCoachActionError(null);
      try {
        if (selectFirst) selectCoachExercise(targetExercise);
        const sid = resolveExerciseStateIdFromRow(
          targetExercise,
          realtimeByTeId
        );
        if (
          sid === TE_STATE_FINALIZED ||
          sid === TE_STATE_CANCELLED
        ) {
          const restartAck = await coachDecisionActions.restartExercise(
            targetExercise.te_id
          );
          if (
            !restartAck ||
            restartAck.ok === false ||
            restartAck.status === "error"
          ) {
            setCoachActionError(
              restartAck?.code ||
                restartAck?.message ||
                "restart_failed"
            );
            return;
          }
        }
        await new Promise((resolve) => {
          coachDecisionActions.startExercise(targetExercise.te_id, (ack) => {
            if (ack?.ok === false) {
              setCoachActionError(ack.code || ack.error_code || "start_failed");
            }
            resolve(ack);
          });
        });
      } finally {
        setRelaunchingFromDecision(false);
      }
    },
    [
      relaunchingFromDecision,
      selectCoachExercise,
      realtimeByTeId,
      coachDecisionActions,
    ]
  );

  const handleDecisionRestart = useCallback(() => {
    if (!currentExercise) return;
    void relaunchCoachExercise(currentExercise);
  }, [currentExercise, relaunchCoachExercise]);

  const handleDecisionPrevious = useCallback(() => {
    const prev = coachStationExercises[coachStationIndex - 1];
    if (!prev) return;
    void relaunchCoachExercise(prev, { selectFirst: true });
  }, [coachStationExercises, coachStationIndex, relaunchCoachExercise]);

  const exerciseNumber = isCoachView
    ? coachStationIndex + 1
    : currentExercise?.te_order ?? (currentIndex >= 0 ? currentIndex + 1 : null);

  const primary = brand?.color_primary || "#0877BC";
  const secondary = brand?.color_secondary || "#46528b";

  const showSessionCompleteOverlay =
    sessionFinalized && !addExerciseOpen && !autoevalExercise;

  const sessionHasCapture = (exercises || []).some((ex) => ex?.capture_status);
  const capturePlayers = presentPlayers.length
    ? presentPlayers
    : (roster || []).filter((player) => player?.user_id != null);

  const captureMoment = useMemo(() => {
    const liveIndex =
      currentExercise?.te_id != null
        ? currentSequenceIndexByTeId[String(currentExercise.te_id)]
        : null;
    const resolved = resolveLiveSequence(currentExercise, liveIndex);
    const active =
      exerciseStateId === TE_STATE_STARTED || exerciseStateId === TE_STATE_PAUSED;
    const between = Boolean(
      awaitingManualSequenceStart || awaitingAutoSequenceStart
    );
    const sequenced =
      isTeSequences(currentExercise) && (currentExercise?.sequences?.length || 0) > 0;
    const sequenceRunning = active && !between && (sequenced || !isTeSequences(currentExercise));
    const phase = resolveCapturePhase({
      sequenceRunning,
      betweenSequences: between,
      sequenceIndex: resolved.idx,
      lastSequenceCompleted,
    });
    return {
      phase,
      sequenceOrder: captureSequenceOrder({
        phase,
        sequenceNumber: resolved.number,
      }),
    };
  }, [
    currentExercise,
    currentSequenceIndexByTeId,
    exerciseStateId,
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    lastSequenceCompleted,
  ]);

  useEffect(() => {
    const onCaption = (event) => {
      setTablePingfloOpen(Boolean(event?.detail?.open));
    };
    window.addEventListener("smarttt:pingflo-caption", onCaption);
    return () => window.removeEventListener("smarttt:pingflo-caption", onCaption);
  }, []);

  useEffect(() => {
    if (!showSessionCompleteOverlay || !sessionHasCapture || !userIsExpertCoach(user)) {
      return undefined;
    }
    if (captureSummaryShownRef.current) return undefined;
    captureSummaryShownRef.current = true;
    setCaptureSummaryOpen(true);
    return undefined;
  }, [showSessionCompleteOverlay, sessionHasCapture, user]);

  const demoPlayerLeaveToNext =
    usePlayerView && (demoClubLive || isDemoWalkInUser(user));

  // Demo players: leave the closed session and wait on /demo for the next one.
  useEffect(() => {
    if (!showSessionCompleteOverlay || !demoPlayerLeaveToNext) return undefined;
    const id = window.setTimeout(() => {
      handleGoDemoNext();
    }, 1200);
    return () => window.clearTimeout(id);
  }, [showSessionCompleteOverlay, demoPlayerLeaveToNext, handleGoDemoNext]);

  const showSessionDecisionOverlay =
    !sessionFinalized &&
    awaitingSessionDecision &&
    !addExerciseOpen &&
    !sessionPlanOpen;

  const overlayPresenceGrp = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("grpinf") || "null");
      const grId = session?.gr_id || stored?.groupDetails?.gr_id || stored?.gr_id;
      if (!grId) return stored;
      return {
        ...(stored || {}),
        groupDetails: {
          ...(stored?.groupDetails || {}),
          gr_id: grId,
        },
      };
    } catch {
      return session?.gr_id ? { groupDetails: { gr_id: session.gr_id } } : null;
    }
  }, [session?.gr_id]);

  const handleFinalizeTrainingSession = useCallback(async () => {
    setFinalizingSession(true);
    try {
      await finalizeTrainingSession();
    } finally {
      setFinalizingSession(false);
    }
  }, [finalizeTrainingSession]);

  const handleExerciseAdded = useCallback(async () => {
    setAddExerciseOpen(false);
    setAddExerciseMode("add");
    setReplaceExerciseTarget(null);
    const reopenPlan = reopenPlanSheetAfterAddRef.current;
    reopenPlanSheetAfterAddRef.current = false;
    reopenSessionAfterAddExercise();
    await refreshExercises();
    await refreshLiveView?.({ silent: true });
    if (reopenPlan) {
      setSessionPlanSheetOpen(true);
    }
  }, [reopenSessionAfterAddExercise, refreshExercises, refreshLiveView]);

  const handlePlanSheetChanged = useCallback(async () => {
    reopenSessionAfterAddExercise();
    await refreshExercises();
    await refreshLiveView?.({ silent: true });
  }, [reopenSessionAfterAddExercise, refreshExercises, refreshLiveView]);

  const openAddExerciseNavigator = useCallback(() => {
    setAddExerciseMode("add");
    setReplaceExerciseTarget(null);
    setAddExerciseOpen(true);
  }, []);

  const handleStartLiveTimer = useCallback(async () => {
    if (!session?.ts_id || startingLiveTimer) return;
    setStartingLiveTimer(true);
    setCoachActionError(null);
    try {
      reopenSessionAfterAddExercise();
      const res = await createLiveTimerBlock({
        ts_id: session.ts_id,
        tsg_id: focusedTsgId || undefined,
      });
      const created = res?.data?.data || {};
      const teId = created.te_id || null;
      const tsgId = created.tsg_id || focusedTsgId || null;
      const list = await refreshExercises();
      await refreshLiveView?.({ silent: true });
      if (tsgId != null && multiStation) {
        selectFocusedStation(tsgId);
      }
      if (teId) {
        const idx = globalIndexForTeId(list || [], teId);
        if (idx >= 0) setCurrentIndex(idx);
        await new Promise((resolve) => {
          coachDecisionActions.startExercise(teId, (ack) => {
            if (ack?.ok === false) {
              setCoachActionError(ack.code || ack.error_code || "start_failed");
            }
            resolve(ack);
          });
        });
      }
      setOpenChronoTick((n) => n + 1);
    } catch (err) {
      setCoachActionError(
        err?.message ||
          t("liveTrainingCoachEmptyTimerError", "Could not start timer block")
      );
    } finally {
      setStartingLiveTimer(false);
    }
  }, [
    session?.ts_id,
    startingLiveTimer,
    focusedTsgId,
    multiStation,
    reopenSessionAfterAddExercise,
    refreshExercises,
    refreshLiveView,
    coachDecisionActions,
    selectFocusedStation,
    setCurrentIndex,
    t,
  ]);

  const openChangeExerciseNavigator = useCallback((ex) => {
    if (!ex?.te_id) return;
    setAddExerciseMode("replace");
    setReplaceExerciseTarget(ex);
    setAddExerciseOpen(true);
  }, []);

  const openAddExerciseFromPlan = useCallback(() => {
    reopenPlanSheetAfterAddRef.current = true;
    setSessionPlanSheetOpen(false);
    openAddExerciseNavigator();
  }, [openAddExerciseNavigator]);

  const openChangeExerciseFromPlan = useCallback(
    (ex) => {
      reopenPlanSheetAfterAddRef.current = true;
      setSessionPlanSheetOpen(false);
      openChangeExerciseNavigator(ex);
    },
    [openChangeExerciseNavigator]
  );

  const closeAddExerciseDialog = useCallback(() => {
    setAddExerciseOpen(false);
    setAddExerciseMode("add");
    setReplaceExerciseTarget(null);
    if (reopenPlanSheetAfterAddRef.current) {
      reopenPlanSheetAfterAddRef.current = false;
      setSessionPlanSheetOpen(true);
    }
  }, []);

  const handleOpenTemplatesFromPlan = useCallback(async () => {
    setSessionPlanSheetOpen(false);
    setTemplateError("");
    setSelectedTemplate(null);
    setLoadingTemplates(true);
    setTemplateDialogOpen(true);
    try {
      const params = {};
      if (user?.coach_id) params.coach_id = user.coach_id;
      if (user?.club_id) params.club_id = user.club_id;
      const response = await getTrainingSessionTemplates(params);
      setTemplates(response?.data?.data || []);
    } catch (err) {
      setTemplates([]);
      setTemplateError(
        err?.message || t("errorLoadingTemplates") || "Error loading templates"
      );
    } finally {
      setLoadingTemplates(false);
    }
  }, [user?.coach_id, user?.club_id, t]);

  const openDigitizeTemplates = useCallback(() => {
    setTemplateApplyMode("capture");
    void handleOpenTemplatesFromPlan();
  }, [handleOpenTemplatesFromPlan]);

  const handleApplyLiveTemplate = useCallback(() => {
    if (!selectedTemplate?.tst_id || !session?.ts_id || !session?.gr_id) return;
    setTemplateError("");
    setTemplateDialogOpen(false);
    setTemplateApplyWizardOpen(true);
  }, [selectedTemplate?.tst_id, session?.ts_id, session?.gr_id]);

  const doApplyLiveTemplate = useCallback(
    async ({ typesOnly = false, cbSlotChoices = null, levelContext = null } = {}) => {
      if (!selectedTemplate?.tst_id || !session?.ts_id || !session?.gr_id) return;
      setApplyingTemplate(true);
      setTemplateError("");
      try {
        const body = {
          tst_id: selectedTemplate.tst_id,
          ts_id: session.ts_id,
          gr_id: session.gr_id,
        };
        if (String(selectedTemplate.tst_kind || "").toLowerCase() === "layout") {
          body.expand_layout = true;
        } else if (focusedTsgId) {
          body.tsg_id = focusedTsgId;
        }
        if (templateApplyMode === "capture") body.capture_mode = true;
        if (typesOnly) {
          body.apply_types_only = true;
        } else if (cbSlotChoices && cbSlotChoices.length > 0) {
          body.cb_slot_choices = cbSlotChoices;
        }
        if (levelContext) body.level_context = levelContext;
        await applyTrainingSessionTemplate(body);
        const appliedCapture = templateApplyMode === "capture";
        setTemplateApplyWizardOpen(false);
        setTemplateDialogOpen(false);
        setSelectedTemplate(null);
        setTemplateApplyMode("catalog");
        reopenSessionAfterAddExercise();
        await refreshExercises();
        await refreshLiveView?.({ silent: true });
        setSessionPlanSheetOpen(!appliedCapture);
      } catch (err) {
        setTemplateError(
          err?.message || t("errorApplyingTemplate") || "Could not apply template"
        );
        setTemplateApplyWizardOpen(true);
      } finally {
        setApplyingTemplate(false);
      }
    },
    [
      selectedTemplate,
      session?.ts_id,
      session?.gr_id,
      focusedTsgId,
      templateApplyMode,
      reopenSessionAfterAddExercise,
      refreshExercises,
      refreshLiveView,
      t,
    ]
  );

  useEffect(() => {
    const onLivePlanRefresh = () => {
      void handlePlanSheetChanged();
    };
    window.addEventListener("smarttt:live-plan-refresh", onLivePlanRefresh);
    return () => {
      window.removeEventListener("smarttt:live-plan-refresh", onLivePlanRefresh);
    };
  }, [handlePlanSheetChanged]);

  const handleSessionPlanApplied = useCallback(async () => {
    setSessionPlanOpen(false);
    reopenSessionAfterAddExercise();
    await refreshExercises();
    await refreshLiveView?.({ silent: true });
    setSessionPlanSheetOpen(true);
  }, [reopenSessionAfterAddExercise, refreshExercises, refreshLiveView]);

  if (!session?.ts_id) {
    const storedGroup = (() => {
      try {
        return JSON.parse(localStorage.getItem("grpinf") || "null");
      } catch {
        return null;
      }
    })();
    const grId = storedGroup?.groupDetails?.gr_id || storedGroup?.gr_id || null;
    if (tourDemo) {
      return (
        <LiveTrainingTourScaffold
          player={storedHomeRole === "player" || !isCoachView}
        />
      );
    }
    return (
      <div
        className="lt-page lt-page--empty"
        style={{ "--lt-brand": primary, "--lt-brand-secondary": secondary }}
      >
        <div className="lt-mobile-only" role="status">
          <h1 className="lt-mobile-only__title">
            {t("liveTrainingTitle", "Live Training")}
          </h1>
          <p className="lt-mobile-only__copy">
            {t("playerNewTrainingSessionHint")}
          </p>
          <button
            type="button"
            className="lt-mobile-only__btn"
            onClick={() => setCreateOpen(true)}
          >
            {t("playerNewTrainingSession")}
          </button>
          <button
            type="button"
            className="lt-mobile-only__btn"
            onClick={handleDashboard}
            style={{ marginTop: 8, background: "transparent", border: "1px solid currentColor" }}
          >
            {t("liveTrainingDashboard", "Dashboard")}
          </button>
        </div>
        <PlayerNewTrainingSessionDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          grId={grId}
          clubId={user?.club_id}
          variant="live"
          onStarted={(session) => {
            const startMs = session?.starts_at
              ? new Date(session.starts_at).getTime()
              : Date.now();
            if (startMs <= Date.now() + 2 * 60 * 1000) {
              setSession(readStoredSession());
            }
            window.dispatchEvent(new Event("smarttt-session-changed"));
          }}
        />
      </div>
    );
  }

  // Players stay on phones. Coaches can open Live Training from the Training
  // page on desktop/tablet to run the session dashboard.
  if (!isMobileLive && usePlayerView) {
    return (
      <div
        className="lt-page lt-page--mobile-only"
        style={{ "--lt-brand": primary, "--lt-brand-secondary": secondary }}
      >
        <div className="lt-mobile-only" role="status">
          <h1 className="lt-mobile-only__title">
            {t("liveTrainingTitle", "Live Training")}
          </h1>
          <p className="lt-mobile-only__copy">
            {t(
              "liveTrainingMobileOnly",
              "Live Training is a phone experience. Open this session on your mobile device to join, record evaluation clips, and follow the table."
            )}
          </p>
          <button
            type="button"
            className="lt-mobile-only__btn"
            onClick={handleDashboard}
          >
            {t("liveTrainingDashboard", "Dashboard")}
          </button>
        </div>
      </div>
    );
  }

  if (loading && !exercises.length) {
    return (
      <div className="lt-page lt-page--loading">
        <Loader />
        <PlayerLiveFullscreenGate enabled={isMobileLive && usePlayerView && !tourDemo} />
      </div>
    );
  }

  return (
    <div
      className={`lt-page${isLandscape ? " lt-page--landscape" : " lt-page--portrait"}`}
      style={{ "--lt-brand": primary, "--lt-brand-secondary": secondary }}
    >
      <PlayerLiveFullscreenGate enabled={isMobileLive && usePlayerView && !tourDemo} />
      {usePlayerView &&
      (evalVideoCaptureEnabled || tablePhoneOn) ? (
        <EvalCameraPermissionBanner
          visible={evalCameraPermission.needsPrompt}
          requesting={evalCameraPermission.requesting}
          onAllow={() => evalCameraPermission.requestPermission()}
          tablePhone={tablePhoneOn}
        />
      ) : null}
      {tablePhoneOn && tablePhoneLimit ? (
        <p className="lt-mobile-only__copy" role="status">
          {t(
            "liveTrainingTablePhoneLimit",
            "This session already has 4 camera phones. Another phone has to leave before this one can record."
          )}
        </p>
      ) : null}
      {isCoachView && (
        <LiveTrainingHeader
          session={session}
          user={user}
          onHome={handleGoHome}
          onRefresh={handleRefreshView}
          onSettings={() => setSettingsOpen(true)}
          refreshing={refreshing}
          isLandscape={isLandscape}
        />
      )}

      {isCoachView && multiStation ? (
        <LiveTrainingStationTabs
          stations={stations}
          focusedTsgId={focusedTsgId}
          onSelectStation={selectFocusedStation}
          realtimeByTeId={realtimeByTeId}
        />
      ) : null}

      {isCoachView && (
        <LiveTrainingProgressRail
          current={coachStationIndex}
          total={coachStationExercises.length || 1}
          finalizedCount={coachStationFinalizedCount}
          exercises={coachStationExercises}
          realtimeByTeId={realtimeByTeId}
          reviewEnabled={demoClubLive}
          onReviewExercise={openDemoExerciseReview}
          selectable
          onSelectExercise={selectCoachExercise}
          onOpenPlan={() => setSessionPlanSheetOpen(true)}
        />
      )}

      <div
        className={`lt-page__body${
          showKotPlayerArena ? " lt-page__body--kot-player" : ""
        }`}
      >
        <div
          className={`lt-page__main${
            showKotPlayerArena ? " lt-page__main--kot-player" : ""
          }`}
        >
          {error ? (
            <p className="lt-page__error" role="alert">
              {error}
            </p>
          ) : null}

          {isCoachView ? (
            <>
              {showCoachEmptySetup ? (
                <LiveTrainingCoachEmptySetup
                  user={user}
                  remainingMs={remainingMs}
                  onStartTimer={handleStartLiveTimer}
                  startingTimer={startingLiveTimer}
                  onPlanSession={() => setSessionPlanSheetOpen(true)}
                  onManagePlayers={() => setOverlayPresenceOpen(true)}
                  onAddExercise={openAddExerciseNavigator}
                  onDigitizeSession={
                    userIsExpertCoach(user) ? openDigitizeTemplates : null
                  }
                />
              ) : coachViewExercise ? (
                <>
                  <LiveTrainingExerciseHero
                    exercise={coachViewExercise}
                    exerciseStateId={coachViewExerciseStateId}
                    exerciseNumber={exerciseNumber}
                    stationName={
                      multiStation
                        ? stationLabel(focusedStation, t)
                        : null
                    }
                    tableLabel={null}
                    opponentName={null}
                    awaitingManualSequenceStart={awaitingManualSequenceStart}
                    awaitingAutoSequenceStart={awaitingAutoSequenceStart}
                    sequenceTransitionPauseRemaining={
                      sequenceTransitionPauseRemaining
                    }
                    currentSequenceIndex={
                      coachViewExercise?.te_id != null
                        ? currentSequenceIndexByTeId[
                            String(coachViewExercise.te_id)
                          ]
                        : null
                    }
                    isCoachView={isCoachView}
                    isKot={coachViewIsKot}
                    kotSnapshot={kotSnapshot}
                    onOpenExerciseDetail={() => setCoachExerciseDetailOpen(true)}
                    onAssignExercise={
                      isTypeOnlyBlock(coachViewExercise)
                        ? () => openChangeExerciseNavigator(coachViewExercise)
                        : null
                    }
                    captureListening={
                      !captureListenPaused &&
                      !tablePingfloOpen &&
                      captureVoiceState !== "unsupported" &&
                      captureVoiceState !== "denied"
                    }
                    captureVoiceState={captureVoiceState}
                    onToggleCaptureListen={() =>
                      setCaptureListenPaused((paused) => !paused)
                    }
                  />
                  <LiveTrainingCaptureListener
                    enabled={
                      isCoachView &&
                      !usePlayerView &&
                      Boolean(coachViewExercise?.capture_status) &&
                      !showSessionCompleteOverlay
                    }
                    paused={captureListenPaused || tablePingfloOpen}
                    tsId={session?.ts_id}
                    teId={coachViewExercise?.te_id}
                    phase={captureMoment.phase}
                    sequenceOrder={captureMoment.sequenceOrder}
                    players={capturePlayers}
                    language={currentAppLanguage()}
                    onState={setCaptureVoiceState}
                  />
                  {coachViewIsKot ? (
                    <LiveTrainingKotCoachParams
                      tsId={session.ts_id}
                      teId={coachViewExercise.te_id}
                      exerciseStateId={coachViewExerciseStateId}
                      kotSnapshot={kotSnapshot}
                    />
                  ) : null}
                  <LiveTrainingCoachDashboard
                    roster={roster}
                    exercise={coachViewExercise}
                    exercises={coachStationExercises}
                    exerciseStateId={coachViewExerciseStateId}
                    isKot={coachViewIsKot}
                    socket={socket}
                    tsId={session.ts_id}
                    grId={session.gr_id}
                    focusedTsgId={focusedTsgId}
                    stations={stations}
                    stationCamerasByTsgId={stationCamerasByTsgId}
                    onSelectStation={selectFocusedStation}
                    kotSnapshot={kotSnapshot}
                    kotRound={kotRound}
                    viewRefreshToken={viewRefreshToken}
                    requestRefresh={requestRefresh}
                    actionError={coachActionError}
                    onActionError={setCoachActionError}
                    onOpenFeedback={openFeedback}
                    chatUnread={chatUnread}
                    onCoachStopRoundReady={registerCoachStopRound}
                    onCoachTableFeedbackSent={handleCoachTableFeedbackSent}
                    coachUserId={user?.user_id}
                    user={user}
                    brandPrimary={primary}
                    brandSecondary={secondary}
                    getHrSamples={getHrSamples}
                    getLiveBpm={getLiveBpm}
                    requestHrSnapshot={requestHrSnapshot}
                    liveSequenceRolesByTeId={liveSequenceRolesByTeId}
                    socketSequenceMetaByTeId={socketSequenceMetaByTeId}
                    currentSequenceIndexByTeId={currentSequenceIndexByTeId}
                    realtimeByTeId={realtimeByTeId}
                    awaitingManualSequenceStart={awaitingManualSequenceStart}
                    awaitingAutoSequenceStart={awaitingAutoSequenceStart}
                    sequenceTransitionPauseRemaining={
                      sequenceTransitionPauseRemaining
                    }
                    lastSequenceCompleted={lastSequenceCompleted}
                    onReviewFinalized={
                      demoClubLive ? openDemoReviewItem : null
                    }
                    onRosterUpdate={() => refreshLiveView({ silent: true })}
                    openPresenceTick={openPresenceTick}
                    openChronoTick={openChronoTick}
                    tablesWithCamera={tablesWithCamera}
                  />
                </>
              ) : null}
            </>
          ) : showKotPlayerArena ? (
            <LiveTrainingKotPanel
              socket={socket}
              tsId={session.ts_id}
              teId={currentExercise?.te_id}
              exerciseStateId={exerciseStateId}
              snapshot={kotSnapshot}
              isCoachView={false}
              viewerPlayerId={viewerPlayerId}
              roster={roster}
              exercise={playerExecutionExercise}
              exerciseNumber={exerciseNumber}
              user={user}
              isLandscape={isLandscape}
              viewerHr={effectiveViewerHr}
              getHrSamples={getHrSamples}
              fullscreen
              scoreTableNumbers={tablePhoneOn ? tablePhoneTables : null}
            />
          ) : usePlayerView ? (
            <>
              <LiveTrainingPlayerView
                key={viewRefreshToken}
                exercise={playerExecutionExercise}
                exerciseStateId={exerciseStateId}
                myTablePlayer={myTablePlayer}
                user={user}
                session={session}
                viewerHr={effectiveViewerHr}
                getHrSamples={getHrSamples}
                viewerMaxHr={viewerMaxHr}
                viewerRole={viewerRole}
                viewerSide={viewerSide}
                viewerPlayerId={viewerPlayerId}
                viewerTableNumber={activeTableNumber}
                viewerTableContext={activeTableContext}
                roster={roster}
                awaitingManualSequenceStart={awaitingManualSequenceStart}
                awaitingAutoSequenceStart={awaitingAutoSequenceStart}
                sequenceTransitionPauseRemaining={
                  sequenceTransitionPauseRemaining
                }
                currentSequenceIndex={
                  currentExercise?.te_id != null
                    ? currentSequenceIndexByTeId[String(currentExercise.te_id)]
                    : null
                }
                isLandscape={isLandscape}
                isKot={isKot}
                onDashboard={isDemoWalkInUser(user) ? null : handleDashboard}
                onRefresh={handlePlayerRefresh}
                refreshing={refreshing}
                tsId={session?.ts_id}
                tsgId={focusedTsgId || currentExercise?.tsg_id || null}
                socket={socket}
                isOffTable={isOffTableExercise(currentExercise)}
                coachTools={coachToolsFromExercise(currentExercise)}
                cameraFacingMode={cameraFacingMode}
                isFrontCamera={isFrontCamera}
                tablePhoneOn={tablePhoneOn}
                stationPhoneOn={stationPhoneOn}
                stationLabel={stationPhoneLabel}
                stationTables={stationRecordingTables}
                instructionRecording={
                  tableInstructionCapture.recording ||
                  tableInstructionCapture.uploading
                }
              />
              {currentExercise?.te_id && session?.ts_id ? (
                <LiveTrainingModeLiveCard
                  socket={socket}
                  tsId={session.ts_id}
                  teId={currentExercise.te_id}
                  viewerPlayerId={viewerPlayerId}
                  viewerUserId={user?.user_id}
                  viewerTableNumber={activeTableNumber}
                />
              ) : null}
              {!isCoachView &&
              isOffTableExercise(currentExercise) &&
              currentExercise?.te_id &&
              user?.user_id ? (
                <div className="lt-page__chrono-review">
                  <LiveTrainingPlayerChronoReview
                    teId={currentExercise.te_id}
                    userId={user.user_id}
                    brandPrimary={primary}
                    brandSecondary={secondary}
                  />
                </div>
              ) : null}
            </>
          ) : showWaiting ? (
            <LiveTrainingWaitingRoom
              remainingMs={remainingMs}
              tableInfo={tableLabel}
              user={user}
            />
          ) : null}
        </div>

        {/* Player actions — settings + feedback FABs (refresh lives in the player header) */}
        {usePlayerView ? (
          <div className="lt-page__actions">
            {evalVideoCaptureEnabled ? (
              <>
                <button
                  type="button"
                  className={`lt-page__fab lt-page__fab--eval${
                    deviceEvalCaptureEnabled ? "" : " lt-page__fab--eval-off"
                  }${
                    deviceEvalCaptureEnabled &&
                    (evalCaptureUi.recording || evalCaptureUi.uploading)
                      ? " lt-page__fab--eval-live"
                      : ""
                  }`}
                  onClick={togglePlayerCameraRecording}
                  aria-pressed={deviceEvalCaptureEnabled}
                  aria-label={
                    deviceEvalCaptureEnabled
                      ? t(
                          "liveTrainingPlayerCameraOn",
                          "Camera recording on"
                        )
                      : t(
                          "liveTrainingPlayerCameraOff",
                          "Camera recording off"
                        )
                  }
                  title={
                    deviceEvalCaptureEnabled
                      ? t(
                          "liveTrainingPlayerCameraOnHint",
                          "Camera recording on — tap to turn off on this device"
                        )
                      : t(
                          "liveTrainingPlayerCameraOffHint",
                          "Camera recording is off — tap to enable on this device"
                        )
                  }
                >
                  {deviceEvalCaptureEnabled ? (
                    <VideocamIcon fontSize="small" />
                  ) : (
                    <VideocamOffIcon fontSize="small" />
                  )}
                  {deviceEvalCaptureEnabled && evalCaptureUi.recording ? (
                    <span className="lt-eval-fab-dot" aria-hidden />
                  ) : null}
                </button>
                {deviceEvalCaptureEnabled ? (
                  <button
                    type="button"
                    className="lt-page__fab"
                    onClick={() => evalOpenCalibrationRef.current?.()}
                    aria-label={t(
                      "liveTrainingVideoCalibration",
                      "Video calibration"
                    )}
                    title={t(
                      "liveTrainingVideoCalibration",
                      "Video calibration"
                    )}
                  >
                    <CameraswitchIcon fontSize="small" />
                  </button>
                ) : null}
                {deviceEvalCaptureEnabled && !isFrontCamera ? (
                  <button
                    type="button"
                    className={`lt-page__fab lt-page__fab--duck${
                      duckModeActive ? " lt-page__fab--duck-active" : ""
                    }`}
                    onClick={toggleDuckMode}
                    aria-pressed={duckModeActive}
                    aria-label={t(
                      "liveTrainingDuckMode",
                      "Duck Mode (Black Screen)"
                    )}
                    title={t(
                      "liveTrainingDuckModeHint",
                      "Turn screen black to save battery while back camera records"
                    )}
                  >
                    <NightlightIcon fontSize="small" />
                  </button>
                ) : null}
              </>
            ) : null}
            <button
              type="button"
              className="lt-page__fab"
              onClick={() => setSettingsOpen(true)}
              aria-label={t("liveTrainingSettings", "Live Training settings")}
              title={t("liveTrainingSettings", "Live Training settings")}
            >
              <SettingsIcon fontSize="small" />
            </button>
            {showKotPlayerArena ? (
              <>
                {/* KoT arena has no player header, so refresh stays here. */}
                <button
                  type="button"
                  className={`lt-page__fab lt-page__fab--refresh${
                    refreshing ? " lt-page__fab--spinning" : ""
                  }`}
                  onClick={handlePlayerRefresh}
                  disabled={refreshing}
                  aria-label={t("liveTrainingRefresh")}
                  title={t("liveTrainingRefresh")}
                >
                  <RefreshIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="lt-page__fab"
                  onClick={handleGoHome}
                  aria-label={t("home") || "Home"}
                  title={t("home") || "Home"}
                >
                  <HomeIcon fontSize="small" />
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        {isCoachView && evalCaptureUi.enabled ? (
          <div className="lt-page__actions lt-page__actions--coach-eval">
            <button
              type="button"
              className={`lt-page__fab lt-page__fab--eval${
                evalCaptureUi.recording || evalCaptureUi.uploading
                  ? " lt-page__fab--eval-live"
                  : ""
              }`}
              onClick={() => evalOpenCalibrationRef.current?.()}
              aria-label={t(
                "liveTrainingVideoCalibration",
                "Video calibration"
              )}
              title={t("liveTrainingVideoCalibration", "Video calibration")}
            >
              <VideocamIcon fontSize="small" />
              {evalCaptureUi.recording ? (
                <span className="lt-eval-fab-dot" aria-hidden />
              ) : null}
            </button>
          </div>
        ) : null}

        {isCoachView ? (
          <LiveTrainingCoachBar
            socket={socket}
            tsId={session.ts_id}
            exercise={coachViewExercise}
            exerciseStateId={coachViewExerciseStateId}
            awaitingManualSequenceStart={awaitingManualSequenceStart}
            awaitingAutoSequenceStart={awaitingAutoSequenceStart}
            sequenceTransitionPauseRemaining={
              sequenceTransitionPauseRemaining
            }
            requestRefresh={requestRefresh}
            onSelectExercise={selectCoachExercise}
            previousExercise={
              coachStationIndex > 0
                ? coachStationExercises[coachStationIndex - 1]
                : null
            }
            nextExercise={
              coachStationIndex < coachStationExercises.length - 1
                ? coachStationExercises[coachStationIndex + 1]
                : null
            }
            hasPrev={coachStationIndex > 0}
            hasNext={coachStationIndex < coachStationExercises.length - 1}
            realtimeByTeId={realtimeByTeId}
            actionError={coachActionError}
            onActionError={setCoachActionError}
            kotRound={coachViewIsKot ? kotRound : null}
            onCoachStopRound={handleCoachStopRound}
            onFinalizeSession={handleFinalizeTrainingSession}
            finalizingSession={finalizingSession}
          />
        ) : null}
      </div>

      <LiveTrainingFeedbackSheet
        open={feedbackOpen}
        onClose={closeFeedback}
        teId={currentExercise?.te_id}
        tsId={session.ts_id}
        userId={user?.user_id}
        peerId={chatPeerId || (!isCoachView ? sessionCoachUserId : null)}
        isCoachView={isCoachView}
        presentPlayers={presentPlayers}
        isLandscape={isLandscape}
        brandPrimary={primary}
        grId={session?.gr_id || null}
        sessionCoachUserId={sessionCoachUserId}
        initialTab={feedbackSessionPlanAssist ? "digiping" : undefined}
        sessionPlanAssist={feedbackSessionPlanAssist}
        liveTraining
        clubId={liveClubId}
        clubName={liveClubName}
        groupName={liveGroupName}
        sessionDate={session?.ts_date || session?.session_date || null}
        sessionDuration={session?.ts_duration || null}
        bootstrapSeed={feedbackBootstrapSeed}
        stationName={
          focusedStation ? stationLabel(focusedStation, t) : null
        }
        exerciseName={
          plannedBlockTitle(currentExercise, localeCode) ||
          localizedExerciseName(currentExercise, localeCode) ||
          null
        }
        tsgId={focusedTsgId || currentExercise?.tsg_id || null}
        presentPlayerNames={sessionPlanPresentNames}
        groupRosterNotPresentNames={sessionPlanNotPresentNames}
        similarLastSession={feedbackSimilarLastSession}
        currentSessionPlan={feedbackCurrentSessionPlan}
      />

      <LiveTrainingAutoevalSheet
        open={Boolean(autoevalExercise)}
        exercise={autoevalExercise}
        playerName={autoevalPlayerName}
        isLandscape={isLandscape}
        smartRequired={autoevalRequired}
        title={
          autoevalRequired
            ? t("ae_live_personalized_title", "Rate your personalized exercise")
            : undefined
        }
        onSubmitted={() => {
          setAutoevalExercise(null);
          setAutoevalRequired(false);
        }}
        onSkip={() => {
          setAutoevalExercise(null);
          setAutoevalRequired(false);
        }}
      />

      <LiveTrainingSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        tsId={session?.ts_id}
        isCoachView={isCoachView}
        sessionTables={sessionTables}
        stations={stations}
        roster={roster}
        exercise={currentExercise}
        socket={socket}
        t={t}
        onSaved={() => setCaptureSettingsTick((n) => n + 1)}
      />

      {deviceEvalCaptureEnabled || tablePhoneOn ? (
        <LiveTrainingEvalCaptureHost
          enabled
          coachDevice={isCoachView}
          tsId={session?.ts_id}
          exercise={isCoachView ? coachViewExercise : currentExercise}
          exerciseStateId={
            isCoachView ? coachViewExerciseStateId : exerciseStateId
          }
          awaitingManualSequenceStart={awaitingManualSequenceStart}
          awaitingAutoSequenceStart={awaitingAutoSequenceStart}
          viewerPlayerId={usePlayerView ? viewerPlayerId : null}
          viewerTableNumber={
            usePlayerView || tablePhoneOn ? activeTableNumber : null
          }
          viewerSide={usePlayerView ? activeCameraSide : null}
          tablePhone={tablePhoneOn}
          tablePlayers={
            tablePhoneOn
              ? tablePhonePlayers?.length
                ? tablePhonePlayers
                : activeTableContext?.players
              : null
          }
          tableOptions={usePlayerView ? sessionTableNumbers : []}
          tablePhoneTables={tablePhoneTables}
          tablePhoneMainTable={tablePhoneOn ? activeTableNumber : null}
          onTablePhoneCoverageChange={updateTablePhoneCoverage}
          kotSnapshot={
            (isCoachView ? coachViewIsKot : isKot) ? kotSnapshot : null
          }
          isLandscape={isLandscape}
          t={t}
          onUiChange={handleEvalCaptureUi}
          socket={socket}
          userId={user?.user_id || null}
        />
      ) : null}

      <LiveTrainingDuckMode
        active={duckModeActive && !isFrontCamera}
        onWake={() => setDuckModeActive(false)}
        tableNumber={activeTableNumber}
        isRecording={Boolean(evalCaptureUi.recording)}
        timerDisplay={
          currentExercise?.sequenceTimer != null
            ? `${Math.floor(Number(currentExercise.sequenceTimer) / 60)}:${String(
                Math.floor(Number(currentExercise.sequenceTimer) % 60)
              ).padStart(2, "0")}`
            : null
        }
        exerciseName={currentExercise?.ex_name || ""}
      />

      {showSessionDecisionOverlay ? (
        <div className="lt-session-complete-overlay" role="presentation">
          <LiveTrainingSessionDecisionPanel
            user={user}
            isCoachView={!usePlayerView}
            onFinalizeSession={handleFinalizeTrainingSession}
            onAddExercise={openAddExerciseNavigator}
            onRestartLastExercise={
              isCoachView ? handleDecisionRestart : null
            }
            onRelaunchPreviousExercise={
              isCoachView ? handleDecisionPrevious : null
            }
            hasPreviousExercise={isCoachView && coachStationIndex > 0}
            finalizing={finalizingSession}
            relaunching={relaunchingFromDecision}
          />
        </div>
      ) : null}

      <LiveTrainingAddExerciseDialog
        open={addExerciseOpen}
        onClose={closeAddExerciseDialog}
        tsId={session?.ts_id}
        groupId={session?.gr_id}
        tsgId={focusedTsgId || null}
        mode={addExerciseMode}
        replaceTeId={replaceExerciseTarget?.te_id || null}
        existingExId={
          replaceExerciseTarget?.ex_id ||
          replaceExerciseTarget?.exercise?.ex_id ||
          null
        }
        presetBlock={replaceExerciseTarget}
        onAdded={handleExerciseAdded}
        t={t}
      />

      <LiveTrainingSessionPlanSheet
        open={sessionPlanSheetOpen && isCoachView}
        onClose={() => setSessionPlanSheetOpen(false)}
        exercises={coachStationExercises}
        stations={stations}
        focusedTsgId={focusedTsgId}
        currentTeId={coachViewExercise?.te_id || null}
        realtimeByTeId={realtimeByTeId}
        tsId={session?.ts_id}
        grId={session?.gr_id}
        onSelectStation={selectFocusedStation}
        onSelectExercise={selectCoachExercise}
        onAddExercise={openAddExerciseFromPlan}
        onChangeExercise={openChangeExerciseFromPlan}
        onAddPlayer={() => {
          setSessionPlanSheetOpen(false);
          if (coachViewExercise) {
            setOpenPresenceTick((n) => n + 1);
          } else {
            setOverlayPresenceOpen(true);
          }
        }}
        onUseTemplate={handleOpenTemplatesFromPlan}
        onAiRecommend={() => {
          setSessionPlanSheetOpen(false);
          setSessionPlanOpen(true);
        }}
        onChanged={handlePlanSheetChanged}
        onOpenPingflo={openSessionPlanPingflo}
        brandPrimary={primary}
        onStationCreated={async ({ created }) => {
          await refreshLiveView?.({ silent: true });
          if (created?.tsg_id) {
            selectFocusedStation(created.tsg_id);
          }
        }}
        t={t}
      />

      <SelectSessionTemplateDialog
        open={templateDialogOpen}
        onClose={() => {
          if (applyingTemplate) return;
          const wasCapture = templateApplyMode === "capture";
          setTemplateDialogOpen(false);
          setSelectedTemplate(null);
          setTemplateApplyMode("catalog");
          if (!wasCapture) setSessionPlanSheetOpen(true);
        }}
        templates={templates}
        selectedTemplate={selectedTemplate}
        onSelectTemplate={setSelectedTemplate}
        onApply={handleApplyLiveTemplate}
        loading={loadingTemplates}
        applying={applyingTemplate}
        container={livePortalRoot || undefined}
      />
      <TemplateApplyWizardDialog
        open={templateApplyWizardOpen}
        template={selectedTemplate}
        tsId={session?.ts_id}
        grId={session?.gr_id}
        teId={currentExercise?.te_id || coachStationExercises?.[0]?.te_id}
        applying={applyingTemplate}
        container={livePortalRoot || undefined}
        onClose={() => {
          if (applyingTemplate) return;
          setTemplateApplyWizardOpen(false);
          setSelectedTemplate(null);
          const wasCapture = templateApplyMode === "capture";
          setTemplateApplyMode("catalog");
          if (!wasCapture) setSessionPlanSheetOpen(true);
        }}
        onBackToSelect={() => {
          if (applyingTemplate) return;
          setTemplateApplyWizardOpen(false);
          setTemplateDialogOpen(true);
        }}
        onConfirm={({ typesOnly, cbSlotChoices, levelContext }) => {
          setTemplateApplyWizardOpen(false);
          void doApplyLiveTemplate({
            typesOnly: !!typesOnly,
            cbSlotChoices: cbSlotChoices || null,
            levelContext: levelContext || null,
          });
        }}
        onError={setTemplateError}
      />
      {templateError && (templateDialogOpen || templateApplyWizardOpen) ? (
        <p className="lt-page__error" role="alert" style={{ position: "fixed", bottom: 24, left: 16, right: 16, zIndex: 4600 }}>
          {templateError}
        </p>
      ) : null}

      <CoachHistorySessionPlanDialog
        open={sessionPlanOpen}
        onClose={() => {
          setSessionPlanOpen(false);
          setSessionPlanSheetOpen(true);
        }}
        tsId={session?.ts_id}
        grId={session?.gr_id}
        tsgId={focusedTsgId}
        targetDuration={Number(session?.ts_duration) || 90}
        language={localeCode}
        onApplied={handleSessionPlanApplied}
        container={livePortalRoot || undefined}
      />

      <LiveTrainingCaptureSummary
        open={captureSummaryOpen && userIsExpertCoach(user)}
        tsId={session?.ts_id}
        lang={(currentAppLanguage() || "en").slice(0, 2)}
        onClose={() => setCaptureSummaryOpen(false)}
        onConfirmed={() => {
          void refreshExercises();
        }}
      />

      {showSessionCompleteOverlay ? (
        <div className="lt-session-complete-overlay" role="presentation">
          <LiveTrainingSessionCompletePanel
            user={user}
            demoNext={demoPlayerLeaveToNext}
            onGoHome={demoPlayerLeaveToNext ? handleGoDemoNext : handleGoHome}
            onAddExercise={
              !usePlayerView && isCoachView ? openAddExerciseNavigator : null
            }
            onManagePresence={
              !usePlayerView && isCoachView
                ? () => {
                    if (currentExercise) {
                      setOpenPresenceTick((n) => n + 1);
                    } else {
                      setOverlayPresenceOpen(true);
                    }
                  }
                : null
            }
            onReviewCapture={
              !usePlayerView && isCoachView && sessionHasCapture
                ? () => setCaptureSummaryOpen(true)
                : null
            }
          />
        </div>
      ) : null}

      {overlayPresenceOpen && session?.ts_id && overlayPresenceGrp ? (
        <PlayerPresenceDialog
          open={overlayPresenceOpen}
          onClose={() => setOverlayPresenceOpen(false)}
          selectedSession={session}
          selectedgrp={overlayPresenceGrp}
          zIndex={1600}
          presentation="sheet"
          presenceCorrectionOnly
          onUpdate={() => refreshLiveView?.({ silent: true })}
        />
      ) : null}

      {isCoachView && coachExerciseDetailOpen && coachViewExercise ? (
        <ExerciseResultDialog
          open
          onClose={() => setCoachExerciseDetailOpen(false)}
          onBack={() => setCoachExerciseDetailOpen(false)}
          exercise={coachViewExercise}
          session={session}
          sessionExercises={coachStationExercises}
          onNavigateExercise={(next) => {
            if (!next?.te_id) return;
            selectCoachExercise(next);
          }}
          viewPlayerId="all"
          localeCode={localeCode}
          viewerParticipatedInSession
        />
      ) : null}

      {isCoachView && demoClubLive && demoReviewTarget?.exercise ? (
        <ExerciseResultDialog
          open
          onClose={() => setDemoReviewTarget(null)}
          onBack={() => setDemoReviewTarget(null)}
          initialTab="feedback"
          exercise={demoReviewTarget.exercise}
          session={session}
          sessionExercises={exercises}
          onNavigateExercise={(next) =>
            setDemoReviewTarget((prev) =>
              next?.te_id ? { exercise: next, sequenceOrder: prev?.sequenceOrder ?? null } : prev
            )
          }
          viewPlayerId="all"
          localeCode={localeCode}
          viewerParticipatedInSession
        />
      ) : null}

      {deviceEvalCaptureEnabled && canSeeEvalCaptureDebug ? (
        <LiveTrainingEvalDebugPanel
          visible
          snapshot={evalCaptureUi.debugSnapshot}
        />
      ) : null}

    </div>
  );
}

export default LiveTrainingPage;
